/**
 * Primer contacto por UTM (spec traffic-attribution): se lee de la URL, se
 * guarda 7 días sin sobrescribir uno vigente y se limpia la barra de direcciones.
 */
import { FIRST_TOUCH_DAYS, parseAttribution, type Attribution } from "@shared/attribution.ts";

const STORAGE_KEY = "gf-acq";
const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const TTL_MS = FIRST_TOUCH_DAYS * 24 * 60 * 60 * 1000;

interface Stored extends Attribution {
  at: number;
}

function readStored(now: number): Attribution | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as Partial<Stored>;
    const acq = parseAttribution(stored.source, stored.campaign);
    if (!acq || typeof stored.at !== "number" || now - stored.at > TTL_MS) return null;
    return acq;
  } catch {
    return null;
  }
}

/** Primer contacto vigente o null. */
export function currentAttribution(now: number = Date.now()): Attribution | null {
  return readStored(now);
}

/** Lee los UTM de la URL actual, guarda el primer contacto si procede y limpia la URL. */
export function captureAttribution(now: number = Date.now()): void {
  if (typeof location === "undefined") return;
  const url = new URL(location.href);
  if (!UTM_PARAMS.some((p) => url.searchParams.has(p))) return;
  const acq = parseAttribution(url.searchParams.get("utm_source"), url.searchParams.get("utm_campaign"));
  if (acq && !readStored(now)) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...acq, at: now } satisfies Stored));
    } catch {
      /* almacenamiento no disponible: se pierde la atribución, nunca el juego */
    }
  }
  for (const p of UTM_PARAMS) url.searchParams.delete(p);
  history.replaceState(history.state, "", `${url.pathname}${url.search}${url.hash}`);
}
